import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime } from '../ui/format';
import { Badge, EmptyState, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { listChannels, listTemplates } from '../inbox/api';
import { listViews } from '../crm/api';
import {
  cancelCampaign,
  createCampaign,
  getCampaign,
  listCampaigns,
  scheduleCampaign,
  type WhatsAppCampaign,
} from './api';

/**
 * WhatsApp campaigns (context.md sections 53-56, 72). Phase 117.
 *
 * The screen makes three things impossible to get wrong by accident, because each of them costs the
 * tenant's phone number rather than a message:
 *
 * - Only official channels are offered. The paired-phone route is for a desk answering customers;
 *   the API refuses a campaign on one, and this does not show it as an option in the first place.
 * - Only approved templates are offered, and only ones belonging to the chosen number.
 * - The pace is shown as a number of messages a minute, with the estimate of how long the whole
 *   list will take underneath it — because "2.000 pessoas" and "20 por minuto" are two facts nobody
 *   multiplies in their head before pressing send.
 */

const TONE: Record<WhatsAppCampaign['status'], BadgeTone> = {
  DRAFT: 'neutral',
  SCHEDULED: 'info',
  SENDING: 'info',
  SENT: 'positive',
  CANCELLED: 'warning',
  FAILED: 'danger',
};

export function WhatsAppCampaignsPage(): React.JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const campaigns = useQuery({ queryKey: ['whatsapp-campaigns'], queryFn: listCampaigns });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['whatsapp-campaigns'] });
  };

  const schedule = useMutation({
    mutationFn: (id: string) => scheduleCampaign(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => cancelCampaign(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<WhatsAppCampaign>[] = [
    {
      key: 'name',
      header: t('whatsappCampaigns.name'),
      render: (campaign) => (
        <span className="cell-stack">
          <button
            type="button"
            className="link-button strong"
            onClick={() => setOpen(open === campaign.id ? null : campaign.id)}
          >
            {campaign.name}
          </button>
          <span className="muted small">
            {t('whatsappCampaigns.pace').replace('{n}', String(campaign.perMinute))}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('whatsappCampaigns.status'),
      render: (campaign) => (
        <span className="cell-stack">
          <Badge tone={TONE[campaign.status]}>
            {t(`whatsappCampaigns.state.${campaign.status}`)}
          </Badge>
          {campaign.failureReason && (
            <span className="muted small">{campaign.failureReason}</span>
          )}
        </span>
      ),
    },
    {
      key: 'progress',
      header: t('whatsappCampaigns.progress'),
      render: (campaign) => (
        <span className="cell-stack">
          <span className="strong">
            {campaign.sentCount}/{campaign.recipientsTotal}
          </span>
          {(campaign.skippedCount > 0 || campaign.failedCount > 0) && (
            <span className="muted small">
              {t('whatsappCampaigns.skipped').replace('{n}', String(campaign.skippedCount))}
              {campaign.failedCount > 0 &&
                ` · ${t('whatsappCampaigns.failed').replace('{n}', String(campaign.failedCount))}`}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'when',
      header: t('whatsappCampaigns.when'),
      secondary: true,
      render: (campaign) =>
        campaign.scheduledAt ? formatDateTime(campaign.scheduledAt) : t('whatsappCampaigns.notYet'),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (campaign) => (
        <span className="cell-inline">
          {campaign.status === 'DRAFT' && (
            <button
              type="button"
              className="button button--primary button--compact"
              onClick={() => schedule.mutate(campaign.id)}
              disabled={schedule.isPending}
            >
              {t('whatsappCampaigns.send')}
            </button>
          )}

          {['DRAFT', 'SCHEDULED', 'SENDING'].includes(campaign.status) && (
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => cancel.mutate(campaign.id)}
              disabled={cancel.isPending}
            >
              {t('whatsappCampaigns.cancel')}
            </button>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="view">
      <PageHeader
        title={t('nav.whatsappCampaigns')}
        description={t('whatsappCampaigns.description')}
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <NewCampaign onCreated={refresh} onError={setError} />

      <Panel>
        <DataTable
          tableKey="whatsapp-campaigns"
          rows={campaigns.data?.data}
          columns={columns}
          rowKey={(campaign) => campaign.id}
          isLoading={campaigns.isLoading}
          error={campaigns.error}
          onRetry={() => void campaigns.refetch()}
          empty={{
            title: t('whatsappCampaigns.emptyTitle'),
            description: t('whatsappCampaigns.emptyBody'),
          }}
        />
      </Panel>

      {open && <Recipients campaignId={open} />}
    </div>
  );
}

/**
 * Who it reached, and who it did not.
 *
 * The skipped list is the point. A campaign that "worked" while quietly dropping four hundred
 * people for want of consent or a name is one nobody can improve, and this is where a desk finds
 * out that its list needs cleaning rather than its message.
 */
function Recipients({ campaignId }: { campaignId: string }): React.JSX.Element {
  const detail = useQuery({
    queryKey: ['whatsapp-campaign', campaignId],
    queryFn: () => getCampaign(campaignId),
  });

  const people = detail.data?.recipients ?? [];

  if (detail.isLoading) return <Panel>{t('state.loading')}</Panel>;

  return (
    <Panel title={t('whatsappCampaigns.recipients')}>
      {people.length === 0 ? (
        <EmptyState title={t('whatsappCampaigns.noRecipients')} />
      ) : (
        <ul className="campaign-people">
          {people.map((person) => (
            <li key={person.entityId}>
              <span className="strong">{person.name ?? person.phone}</span>
              <span className="muted small">{person.phone}</span>
              <Badge
                tone={
                  person.status === 'SENT'
                    ? 'positive'
                    : person.status === 'FAILED'
                      ? 'danger'
                      : person.status === 'SKIPPED'
                        ? 'warning'
                        : 'neutral'
                }
              >
                {t(`whatsappCampaigns.recipient.${person.status}`)}
              </Badge>
              {person.reason && <span className="muted small">{person.reason}</span>}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function NewCampaign({
  onCreated,
  onError,
}: {
  onCreated: () => void;
  onError: (message: string | null) => void;
}): React.JSX.Element {
  const [name, setName] = useState('');
  const [channelId, setChannelId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [viewId, setViewId] = useState('');
  const [perMinute, setPerMinute] = useState(20);
  const [values, setValues] = useState<string[]>([]);

  const channels = useQuery({ queryKey: ['channels'], queryFn: listChannels, staleTime: 60_000 });
  const templates = useQuery({
    queryKey: ['whatsapp-templates', channelId],
    queryFn: () => listTemplates(channelId),
    enabled: channelId !== '',
  });
  const views = useQuery({ queryKey: ['views', 'CONTACT'], queryFn: () => listViews('CONTACT') });

  /*
   * Only a number that can actually run a campaign.
   *
   * The official Business API, because the unofficial route is a desk's phone and a list of two
   * thousand is what gets it banned.
   */
  const official = (channels.data?.channels ?? []).filter(
    (channel) => channel.kind === 'WHATSAPP' && channel.provider === 'whatsapp_cloud',
  );

  const approved = (templates.data?.data ?? []).filter(
    (template) => template.status === 'APPROVED' && template.channel_id === channelId,
  );

  const template = approved.find((candidate) => candidate.id === templateId);
  const variables = template?.parameters.BODY ?? 0;

  const create = useMutation({
    mutationFn: () =>
      createCampaign({
        name,
        channelId,
        templateId,
        values: variables > 0 ? { BODY: values.slice(0, variables) } : {},
        viewId,
        perMinute,
      }),
    onSuccess: () => {
      onError(null);
      setName('');
      setTemplateId('');
      setValues([]);
      onCreated();
    },
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  const ready = name.trim() !== '' && channelId !== '' && templateId !== '' && viewId !== '';

  const minutes = useMemo(() => Math.ceil(2000 / Math.max(perMinute, 1)), [perMinute]);

  return (
    <Panel title={t('whatsappCampaigns.new')}>
      {official.length === 0 ? (
        <EmptyState
          title={t('whatsappCampaigns.noChannelTitle')}
          description={t('whatsappCampaigns.noChannelBody')}
        />
      ) : (
        <div className="form-grid">
          <label className="field">
            <span className="field__label">{t('whatsappCampaigns.name')}</span>
            <input
              className="input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={160}
            />
          </label>

          <label className="field">
            <span className="field__label">{t('whatsappCampaigns.channel')}</span>
            <select
              className="select"
              value={channelId}
              onChange={(event) => {
                setChannelId(event.target.value);
                setTemplateId('');
              }}
            >
              <option value="">—</option>
              {official.map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">{t('whatsappCampaigns.template')}</span>
            <select
              className="select"
              value={templateId}
              onChange={(event) => setTemplateId(event.target.value)}
              disabled={channelId === ''}
            >
              <option value="">—</option>
              {approved.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.name} ({candidate.language})
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">{t('whatsappCampaigns.audience')}</span>
            <select
              className="select"
              value={viewId}
              onChange={(event) => setViewId(event.target.value)}
            >
              <option value="">—</option>
              {(views.data?.views ?? []).map((view) => (
                <option key={view.id} value={view.id}>
                  {view.name}
                </option>
              ))}
            </select>
          </label>

          {/*
           * One field per template variable, with the placeholders named.
           *
           * A placeholder nothing can fill skips that person rather than sending the braces, and
           * saying so here is cheaper than saying it on the report afterwards.
           */}
          {Array.from({ length: variables }, (_, index) => (
            <label className="field" key={index}>
              <span className="field__label">
                {t('whatsappCampaigns.variable').replace('{n}', String(index + 1))}
              </span>
              <input
                className="input"
                value={values[index] ?? ''}
                placeholder="{primeiro_nome}"
                onChange={(event) =>
                  setValues((current) => {
                    const next = [...current];
                    next[index] = event.target.value;
                    return next;
                  })
                }
              />
              <span className="muted small">{t('whatsappCampaigns.placeholders')}</span>
            </label>
          ))}

          <label className="field">
            <span className="field__label">{t('whatsappCampaigns.perMinute')}</span>
            <input
              className="input"
              type="number"
              min={1}
              max={600}
              value={perMinute}
              onChange={(event) => setPerMinute(Number(event.target.value) || 1)}
            />
            <span className="muted small">
              {t('whatsappCampaigns.paceHint').replace('{m}', String(minutes))}
            </span>
          </label>

          <div className="form-actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => create.mutate()}
              disabled={!ready || create.isPending}
            >
              {t('whatsappCampaigns.create')}
            </button>
          </div>
        </div>
      )}
    </Panel>
  );
}
